import { Components } from '@mui/material/styles';
import { Theme } from '@mui/material';
import { getFocusRingStyle, updateFocusRingStyle } from '@/src/components/focusring/focusring';
import { getInteractiveStyle } from '@/src/materialui/button/interactive';

export const flowIconButton: Components<Theme>['MuiIconButton'] = {
  defaultProps: {
    disableRipple: true,
    variant: 'standard',
    color: 'secondary',
    size: 'medium',
  },
  styleOverrides: {
    root: ({ theme }) => ({
      position: 'relative',
      boxShadow: 'none',
      outline: 'none',
      width: '44px',
      height: '44px',
      borderRadius: '1000px',
      maxWidth: 'none',
      transition: 'all 0.3s ease-out',
      flexShrink: '0',
      '&:hover': {
        boxShadow: 'none',
      },
      '&&:active': {
        boxShadow: 'none',
      },
      ...getInteractiveStyle(
        {
          backgroundColor: 'transparent',
        },
        {
          // hover effect also in accordionSummary.theme.tsx
          backgroundColor: theme.palette.transparent.secondary,
        },
      ),
      // focused style
      ...getFocusRingStyle(theme),
      // disabled style
      '&.Mui-disabled': {
        opacity: '1',
        color: theme.palette.disabled.primary,
      },
    }),
    sizeSmall: {
      height: '32px',
      width: '32px',
      // icon itself
      '& .MuiSvgIcon-root': {
        fontSize: '17px',
      },
    },
    sizeMedium: {
      // width & height & borderRadius is default in root
      // icon itself
      '& .MuiSvgIcon-root': {
        fontSize: '20px',
      },
    },
    sizeLarge: {
      height: '50px',
      width: '50px',
      // icon itself
      '& .MuiSvgIcon-root': {
        fontSize: '22px',
      },
    },
  },
  variants: [
    {
      props: { color: 'secondary' },
      style: ({ theme }) => ({
        ...getInteractiveStyle(
          {
            color: theme.palette.text.primary,
          },
          {
            color: theme.palette.text.primary,
          },
        ),
      }),
    },
    {
      props: { variant: 'filled' },
      style: ({ theme }) => ({
        // disabled style
        '&.Mui-disabled': {
          opacity: '1',
          backgroundColor: theme.palette.disabled.secondary,
          color: theme.palette.disabled.tertiary,
        },
      }),
    },
    {
      props: { variant: 'filled', color: 'primary' },
      style: ({ theme }) => ({
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.brand.secondary,
            color: theme.palette.onSurface.os900,
          },
          {
            backgroundColor: theme.palette.brandHover.secondary,
            color: theme.palette.onSurface.os900,
          },
        ),
      }),
    },
    {
      props: { variant: 'filled', color: 'secondary' },
      style: ({ theme }) => ({
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.secondary.contrastText,
          },
          {
            backgroundColor: theme.palette.secondary.light,
            color: theme.palette.secondary.contrastText,
          },
        ),
      }),
    },
    {
      props: { variant: 'filled', color: 'error' },
      style: ({ theme }) => ({
        ...getInteractiveStyle(
          {
            color: theme.palette.error.contrastText,
            backgroundColor: theme.palette.error.main,
          },
          {
            color: theme.palette.error.contrastText,
            backgroundColor: theme.palette.error.dark,
          },
        ),
      }),
    },
    {
      props: { variant: 'filled', color: 'inherit' },
      style: ({ theme }) => ({
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.transparent.secondary,
          },
          {
            backgroundColor: theme.palette.surface.primary,
          },
        ),
      }),
    },
    {
      props: { variant: 'outlined' },
      style: {
        border: '2px solid currentColor',
        ...updateFocusRingStyle(2),
      },
    },
    {
      props: { variant: 'outlined', size: 'small' },
      style: {
        borderWidth: '1px',
        ...updateFocusRingStyle(1),
      },
    },
    {
      props: { color: 'surface' },
      style: ({ theme }) => ({
        border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.transparent.quarternary : theme.palette.onSurface.os100}`,
        ...updateFocusRingStyle(1),
        ...getInteractiveStyle(
          {
            backgroundColor: theme.palette.surface.primary,
            color: theme.palette.text.primary,
          },
          {
            backgroundColor: theme.palette.surface.secondary,
            color: theme.palette.text.primary,
          },
        ),
        // disabled style
        '&.Mui-disabled': {
          borderColor: theme.palette.surface.secondary,
          color: theme.palette.disabled.primary,
        },
      }),
    },
    {
      props: { color: 'surface', variant: 'outlined' },
      style: {
        borderWidth: '2px',
        ...updateFocusRingStyle(2),
      },
    },
    {
      props: { color: 'white' },
      style: ({ theme }) => ({
        // hover effect also in accordionSummary.theme.tsx
        // hover effect also in FooterAccordion.tsx
        ...getInteractiveStyle(
          {
            backgroundColor: 'transparent',
            color: theme.palette.onSurface.os0,
          },
          {
            color: theme.palette.onSurface.os0,
            backgroundColor: theme.palette.transparent.tertiary,
          },
        ),
        // disabled style
        '&.Mui-disabled': {
          borderColor: theme.palette.disabled.primary,
          color: theme.palette.disabled.primary,
        },
      }),
    },
  ],
};
